import { useState, type FormEvent } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { MailCheck } from 'lucide-react'
import { requestPasswordReset, resetPassword } from '@/lib/auth-client'
import { getLocalizedPath } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'
import { AuthCard, AuthField, AuthNotice, authButtonClass, authLinkClass } from '@/pages/auth-ui'

// Stessi limiti di Better Auth (minPasswordLength/maxPasswordLength di default).
const PASSWORD_MIN = 8
const PASSWORD_MAX = 128

const RESET_ERROR_KEYS: Record<string, string> = {
  INVALID_TOKEN: 'auth.errors.linkExpired',
  PASSWORD_TOO_SHORT: 'auth.errors.passwordTooShort',
}

type Props = { onBack: () => void }

/** Richiesta del link: la risposta è la stessa che l'indirizzo sia registrato o no. */
export function ForgotPasswordForm({ onBack, expired }: Props & { expired: boolean }) {
  const { t, i18n } = useTranslation()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(expired ? t('auth.errors.linkExpired') : null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const email = String(new FormData(event.currentTarget).get('email')).trim()
    setError(null)
    setIsSubmitting(true)
    const { error: requestError } = await requestPasswordReset({
      email,
      redirectTo: getLocalizedPath('login', i18n.language as SupportedLanguage),
    })
    setIsSubmitting(false)
    if (requestError) {
      setError(t(requestError.status === 429 ? 'auth.errors.tooManyAttempts' : 'auth.errors.generic'))
      return
    }
    setSentTo(email)
  }

  if (sentTo) {
    return (
      <AuthCard
        headingId="forgot-heading"
        title={t('auth.forgot.sentTitle')}
        onBack={onBack}
        icon={
          <span className="flex size-12 items-center justify-center rounded-full bg-sea/15 ring-1 ring-inset ring-sea/50">
            <MailCheck aria-hidden className="size-6 text-sea-ink" />
          </span>
        }
      >
        <div>
          <p role="status" className="text-sm leading-relaxed text-ink">
            <Trans i18nKey="auth.forgot.sent" values={{ email: sentTo }} components={{ strong: <strong className="font-semibold text-ink" /> }} />
          </p>
          <p className="mt-4 border-t border-line pt-4 text-xs leading-relaxed text-ink-soft">
            {t('auth.forgot.sentHint')}{' '}
            <button type="button" onClick={() => setSentTo(null)} className={`text-xs ${authLinkClass} text-ink underline`}>
              {t('auth.forgot.resend')}
            </button>
          </p>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard headingId="forgot-heading" title={t('auth.forgot.title')} intro={t('auth.forgot.intro')} onBack={onBack} onSubmit={handleSubmit}>
      {error && (
        <div className="mb-4">
          <AuthNotice tone="error">{error}</AuthNotice>
        </div>
      )}
      <AuthField label={t('auth.fields.email')} name="email" type="email" autoComplete="email" autoFocus required />
      <button type="submit" disabled={isSubmitting} className={`mt-6 w-full ${authButtonClass}`}>
        {isSubmitting ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
      </button>
    </AuthCard>
  )
}

/**
 * Nuova password dal link ricevuto via email (`?token=` nella pagina di login). Con `invite` è il link
 * d'invito: stessi token e flusso, testi da attivazione account.
 */
export function ResetPasswordForm({ token, invite = false, onBack, onDone }: Props & { token: string; invite?: boolean; onDone: () => void }) {
  const texts = invite ? 'auth.invite' : 'auth.reset'
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const newPassword = String(form.get('password'))
    if (newPassword !== String(form.get('confirmPassword'))) {
      setError(t('auth.errors.passwordMismatch'))
      return
    }
    setError(null)
    setIsSubmitting(true)
    const { error: resetError } = await resetPassword({ newPassword, token })
    setIsSubmitting(false)
    if (resetError) {
      setError(t(RESET_ERROR_KEYS[resetError.code ?? ''] ?? 'auth.errors.generic'))
      return
    }
    onDone()
  }

  const passwordProps = { type: 'password', autoComplete: 'new-password', minLength: PASSWORD_MIN, maxLength: PASSWORD_MAX, required: true }
  return (
    <AuthCard headingId="reset-heading" title={t(`${texts}.title`)} intro={t(`${texts}.intro`)} onBack={onBack} onSubmit={handleSubmit}>
      {error && (
        <div className="mb-4">
          <AuthNotice tone="error">{error}</AuthNotice>
        </div>
      )}
      <div className="space-y-4">
        <AuthField
          label={t('auth.fields.newPassword')}
          name="password"
          hint={t('auth.fields.passwordRule', { count: PASSWORD_MIN })}
          autoFocus
          {...passwordProps}
        />
        <AuthField label={t('auth.fields.confirmPassword')} name="confirmPassword" {...passwordProps} />
      </div>
      <button type="submit" disabled={isSubmitting} className={`mt-6 w-full ${authButtonClass}`}>
        {isSubmitting ? t('auth.reset.submitting') : t(`${texts}.submit`)}
      </button>
    </AuthCard>
  )
}
